/** @prettier */

import {memo, useEffect, useState} from 'react';
import {
  Card,
  CardContent,
  Grid,
  Paper,
  CardHeader,
  Stack,
  Box,
} from '@mui/material';
import {useTranslation} from 'react-i18next';

//MTH component import
import GraphHeader from './GraphAppBar';
import BarChartGraph from '../../StackBarChart';
import LinearWithValueLabel from '../../LinearProgressWithLabel';
import {StaticDatePicker, StaticComparisonDatePicker} from './DatePicker';
import TextFieldSelectBox from '../../SelectBox';
import {PowerTrendSelectButton} from './molculate/PowerTrendButton';
import {AddButton} from '../../BasicButton';
import {SLLineGraphDisplay} from './GraphDisplay';
//type import
import {GraphFormType, GraphDataRange, graphProps} from '../../../Type/TypeDef';
//Style import
import '../../../css/common/common.css';

import {useInteractJS} from '../../../functions/positionMove';
import {makeGraphData} from '../../../functions/makeGraphData';
import {useGetStartEndDate} from '../../../hooks/graph/useGetStartEndDate';
import {useGroupChangeStartEndDate} from '../../../hooks/graph/useGroupChangeStartEndDate';
import {pullDownMenu} from '../../../define';
import {makeXLabel} from '../../../functions/graphFunction';

/**
 * グループグラフ
 */
const GroupGraph = memo((props: {graphProps: graphProps}) => {
  const trans = useTranslation().t; //翻訳関数
  //prettier-ignore
  const {siteID, graphTitle, graphTarget, graphTargetID, graphType, setGraphParam} = props.graphProps;

  //日付の表示（1日/1週間/1か月/1年/10年）
  const [dateRange, setDateRange] = useState<GraphDataRange>('day');
  //prettier-ignore
  const [barGraphDataCool, setBarGraphDataCool] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータ冷房
  //prettier-ignore
  const [comparisonBarGraphDataCool, setComparisonBarGraphDataCool] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータ冷房（比較日）
  //prettier-ignore
  const [barGraphDataHot, setBarGraphDataHot] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータ暖房
  //prettier-ignore
  const [comparisonBarGraphDataHot, setComparisonBarGraphDataHot] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータ暖房（比較日）
  //prettier-ignore
  const [barGraphDataOther, setBarGraphDataOther] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータその他
  //prettier-ignore
  const [comparisonBarGraphDataOther, setComparisonBarGraphDataOther] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータその他（比較日）
  //prettier-ignore
  const [trendGraphIndoorTemperature, setTrendGraphIndoorTemperature] = useState<ApexAxisChartSeries['0']['data']>([]); //折れ線グラフデータ 室内温度
  //prettier-ignore
  const [trendGraphOutdoorTemperature, setTrendGraphOutdoorTemperature] = useState<ApexAxisChartSeries['0']['data']>([]); //折れ線グラフデータ 室外温度
  //prettier-ignore
  const [trendGraphPresetTemperature, setTrendGraphPresetTemperature] = useState<ApexAxisChartSeries['0']['data']>([]); //折れ線グラフデータ 設定温度
  //prettier-ignore
  const [comparisonTrendGraphIndoorTemp, setComparisonTrendGraphIndoorTemp] = useState<ApexAxisChartSeries['0']['data']>([]); //折れ線グラフデータ 室内温度（比較日）
  //prettier-ignore
  const [comparisonTrendGraphOutdoorTemp, setComparisonTrendGraphOutdoorTemp] = useState<ApexAxisChartSeries['0']['data']>([]); //折れ線グラフデータ 室外温度（比較日）
  //prettier-ignore
  const [comparisonTrendGraphPresetTemp, setComparisonTrendGraphPresetTemp] = useState<ApexAxisChartSeries['0']['data']>([]); //折れ線グラフデータ 設定温度（比較日）
  const [xDate, setXDate] = useState<string[] | []>([]); //通常日の日付データ
  const [comparisonXDate, setComparisonXDate] = useState<string[]>([]); //比較日の日付データ
  const [xAxisLabel, setXAxisLabel] = useState<string[]>([]); //x軸に表示するラベル
  //prettier-ignore
  const [comparisonXAxisLabel, setComparisonXAxisLabel] = useState<string[]>([]); //比較グラフのx軸に表示するラベル
  const [activeComparison, setActiveComparison] = useState(false); //比較データ表示判定

  const interact = useInteractJS(); //グラフダイアログの移動に必要な関数

  //prettier-ignore
  //グラフに表示する日付の範囲を取得
  const {timeZone, graphStartDate, graphEndDate, comparisonGraphStartDate, comparisonGraphEndDate, setGraphDispDate, setComparisonGraphDispDate} = useGetStartEndDate(siteID)

  //日付や対象が変更されたとき、グラフに描画するデータを再取得
  const {groupData, fileNameDate, graphDispError, apiWait} =
    useGroupChangeStartEndDate(
      siteID,
      graphStartDate,
      graphEndDate,
      graphType,
      timeZone,
      dateRange
    );
  const {
    groupData: comparisonGroupData,
    fileNameDate: comparisonFileNameDate,
    graphDispError: comparisonGraphDispError,
    apiWait: comparisonApiWait,
  } = useGroupChangeStartEndDate(
    siteID,
    comparisonGraphStartDate,
    comparisonGraphEndDate,
    graphType,
    timeZone,
    dateRange
  );

  //受信APIデータ変更時、グラフ表示項目変更時にグラフ描画用データを作成する
  useEffect(() => {
    if (groupData === undefined) return;
    const ret = makeGraphData(
      dateRange,
      groupData,
      graphTargetID,
      graphType,
      timeZone,
      'group-states',
      'states',
      'group-id',
      graphStartDate,
      graphEndDate,
      trans
    );

    setXDate(ret.date);
    setTrendGraphIndoorTemperature(ret.tempInY);
    setTrendGraphOutdoorTemperature(ret.tempOutY);
    setTrendGraphPresetTemperature(ret.tempPreY);
    setBarGraphDataCool(ret.powerCool);
    setBarGraphDataHot(ret.powerHot);
    setBarGraphDataOther(ret.powerOther);
  }, [groupData, trans]);

  //比較ボタン押下時に、別の日のグラフ描画用データを作成する
  useEffect(() => {
    if (comparisonGroupData === undefined) return;
    const comparisonRet = makeGraphData(
      dateRange,
      comparisonGroupData,
      graphTargetID,
      graphType,
      timeZone,
      'group-states',
      'states',
      'group-id',
      comparisonGraphStartDate,
      comparisonGraphEndDate,
      trans
    );

    setComparisonXDate(comparisonRet.date);
    setComparisonTrendGraphIndoorTemp(comparisonRet.tempInY);
    setComparisonTrendGraphOutdoorTemp(comparisonRet.tempOutY);
    setComparisonTrendGraphPresetTemp(comparisonRet.tempPreY);
    setComparisonBarGraphDataCool(comparisonRet.powerCool);
    setComparisonBarGraphDataHot(comparisonRet.powerHot);
    setComparisonBarGraphDataOther(comparisonRet.powerOther);
  }, [comparisonGroupData, trans]);

  //x軸に表示するラベルを作成する
  useEffect(() => {
    makeXLabel({
      xDate: xDate,
      comparisonXDate: comparisonXDate,
      dateRange: dateRange,
      graphType: graphType,
      activeComparison: activeComparison,
      setXAxisLabel: setXAxisLabel,
      setComparisonXAxisLabel: setComparisonXAxisLabel,
    });
  }, [xDate, comparisonXDate, activeComparison]);

  //表示範囲が1ヵ月間、または1年間の状態で折れ線グラフへ移動した場合、グラフ表示範囲を1日に変更
  useEffect(() => {
    if (
      graphType === 'line' &&
      (dateRange === 'month' || dateRange === 'year' || dateRange === 'tenYear')
    ) {
      setDateRange('day');
    }
  }, [graphType]);

  //データ取得範囲で10年が選択された際に、比較機能をOFFにする
  useEffect(() => {
    if (dateRange === 'tenYear') {
      setActiveComparison(false);
    }
  }, [dateRange]);

  /** 比較機能のON/OFFを切り替える関数 */
  const handleOnComparison = () => {
    setActiveComparison(!activeComparison);
  };

  /**
   * グラフ形式を決定
   * @param type グラフ形式
   */
  const selectPowerTrend = (type: GraphFormType) => {
    setGraphParam(graphTarget, graphTargetID, type);
  };

  /**
   * グラフ
   * @returns JSX.Element
   */
  const GraphDisplay = () => {
    //折れ線グラフの場合
    if (graphType === 'line') {
      return (
        <SLLineGraphDisplay
          dateRange={dateRange}
          activeComparison={activeComparison}
          fileNameDate={
            activeComparison
              ? `${fileNameDate},${comparisonFileNameDate}`
              : fileNameDate
          }
          graphTitle={graphTitle}
          graphTarget={graphTarget}
          xAxisLabel={xAxisLabel}
          comparisonXAxisLabel={comparisonXAxisLabel}
          trendGraphIndoorTemp={trendGraphIndoorTemperature}
          trendGraphOutdoorTemp={trendGraphOutdoorTemperature}
          trendGraphPresetTemp={trendGraphPresetTemperature}
          comparisonTrendGraphIndoorTemp={comparisonTrendGraphIndoorTemp}
          comparisonTrendGraphOutdoorTemp={comparisonTrendGraphOutdoorTemp}
          comparisonTrendGraphPresetTemp={comparisonTrendGraphPresetTemp}
        />
      );
    }

    //棒グラフの場合
    if (barGraphDataCool.length === 0) return <></>;
    if (barGraphDataHot.length === 0) return <></>;
    if (activeComparison) {
      return (
        <BarChartGraph
          graphMode="Individual"
          xLabel={xAxisLabel}
          graphTarget={graphTarget}
          dateRange={dateRange}
          csvFileNameDate={`${fileNameDate},${comparisonFileNameDate}`} //csvファイル名の日付(グラフタイトルとは表示形式が異なる)
          csvFileNameTarget={graphTitle} //グラフタイトルとcsvファイル名
          barGraphData1={barGraphDataCool}
          barGraphData2={barGraphDataHot}
          barGraphData3={barGraphDataOther}
          comparisonBarGraphData1={comparisonBarGraphDataCool}
          comparisonBarGraphData2={comparisonBarGraphDataHot}
          comparisonBarGraphData3={comparisonBarGraphDataOther}
        />
      );
    }
    return (
      <BarChartGraph
        graphMode="Individual"
        xLabel={xAxisLabel}
        graphTarget={graphTarget}
        dateRange={dateRange}
        csvFileNameDate={fileNameDate} //csvファイル名の日付(グラフタイトルとは表示形式が異なる)
        csvFileNameTarget={graphTitle} //グラフタイトルとcsvファイル名
        barGraphData1={barGraphDataCool}
        barGraphData2={barGraphDataHot}
        barGraphData3={barGraphDataOther}
      />
    );
  };

  return (
    <Paper
      elevation={10}
      className="graph"
      ref={interact.ref}
      style={{...interact.style}}>
      <Card className="graph__card">
        <CardHeader
          disableTypography
          title={
            <GraphHeader
              setGraphParam={setGraphParam}
              graphTitle={graphTitle}
            />
          }
          className="graph__card__header"
        />
        <CardContent
          //折れ線グラフかつ週比較の場合、スクロールバーを表示する
          className={`graph__card__main ${
            graphType === 'line' && dateRange === 'week' && activeComparison
              ? 'graph__card__main--line'
              : ''
          }`}>
          {/* 消費電力とトレンドグラフの選択 */}
          <PowerTrendSelectButton
            graphType={graphType}
            selectPowerTrend={selectPowerTrend}
          />
          {/* グラフデータ設定欄 */}
          {timeZone !== '' && (
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              className="graph__card__main__menu">
              <Grid item xs="auto" container alignItems="center" spacing={2}>
                {/* カレンダー */}
                <Grid item xs="auto">
                  <StaticDatePicker
                    setGraphDisplayDate={setGraphDispDate}
                    timeZone={timeZone}
                    dateRange={dateRange}
                  />
                </Grid>
                {/* 比較用カレンダー */}
                <Grid item xs>
                  {activeComparison ? (
                    <StaticComparisonDatePicker
                      setGraphDisplayDate={setComparisonGraphDispDate}
                      timeZone={timeZone}
                      dateRange={dateRange}
                      onCloseClick={handleOnComparison}
                    />
                  ) : (
                    dateRange !== 'tenYear' && (
                      //比較用カレンダー表示ボタン
                      <AddButton
                        onClick={handleOnComparison}
                        buttonText={trans('SiteGraph.buttonTxt.comparison')}
                      />
                    )
                  )}
                </Grid>
              </Grid>
              <Grid item xs="auto">
                {/* グラフ表示期間設定セレクトボックス */}
                {graphType === 'line' ? (
                  <TextFieldSelectBox
                    label={trans('GroupGraph.txtField.label.graphRange')}
                    choices={pullDownMenu.LINE_GRAPH_RANGE}
                    value={dateRange}
                    setValue={setDateRange}
                    id={'graphRangeSelect'}
                    class="graph__card__main__menu__selectBox"
                  />
                ) : (
                  <TextFieldSelectBox
                    label={trans('GroupGraph.txtField.label.graphRange')}
                    choices={pullDownMenu.GRAPH_RANGE}
                    value={dateRange}
                    setValue={setDateRange}
                    id={'graphRangeSelect'}
                    class="graph__card__main__menu__selectBox"
                  />
                )}
              </Grid>
            </Grid>
          )}
          {/* グラフ */}
          {apiWait || comparisonApiWait ? (
            <Stack justifyContent="center" alignItems="center">
              {graphDispError || comparisonGraphDispError ? (
                <h1 className="graph__card__main__errorMessage">
                  {trans('GroupGraph.errMsg.gettingDataFailure')}
                </h1>
              ) : (
                <Box className="graph__card__main__progress">
                  <LinearWithValueLabel />
                </Box>
              )}
            </Stack>
          ) : graphDispError || comparisonGraphDispError ? (
            <Stack justifyContent="center" alignItems="center">
              <h1 className="graph__card__main__errorMessage">
                {trans('GroupGraph.errMsg.gettingDataFailure')}
              </h1>
            </Stack>
          ) : (
            <GraphDisplay />
          )}
        </CardContent>
      </Card>
    </Paper>
  );
});
export default GroupGraph;
